import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { api, ApiClientError } from '../lib/api';
import { useCurrentUser } from '../lib/useCurrentUser';

/**
 * ArchiveButton — the §9 archive / unarchive control for a property or parcel
 * detail page. Archiving is a terminal, org-scoped state (archived_at); the
 * server enforces the rules (a property can only be archived once its appeals are
 * complete, archiving auto-archives its parcels) and returns a 409 with a plain
 * message when a rule blocks it — surfaced here as an error toast.
 *
 * Visible only to the roles allowed to archive (super_admin + office
 * administrator); the server is the real gate, this just hides a dead control.
 */

const ARCHIVE_ROLES = new Set(['super_admin', 'administrator']);

export interface ArchiveButtonProps {
  /** Route segment: 'properties' | 'parcels'. */
  entityType: 'properties' | 'parcels';
  entityId: string;
  /** Whether the record is currently archived (archived_at set). */
  archived: boolean;
  /** Query keys to invalidate after a successful archive/unarchive. */
  invalidateKeys?: unknown[][];
  size?: 'sm' | 'md' | 'lg';
}

export function ArchiveButton({ entityType, entityId, archived, invalidateKeys = [], size = 'sm' }: ArchiveButtonProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const currentUser = useCurrentUser();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const noun = entityType === 'properties' ? 'property' : 'parcel';

  const mutation = useMutation({
    mutationFn: () => api.post(`/api/${entityType}/${entityId}/${archived ? 'unarchive' : 'archive'}`, {}),
    onSuccess: () => {
      setConfirmOpen(false);
      invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      toast({ variant: 'success', message: archived ? `Restored the ${noun}.` : `Archived the ${noun}.` });
    },
    onError: (err) => toast({
      variant: 'error',
      message: err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : `Could not ${archived ? 'restore' : 'archive'} the ${noun}.`,
    }),
  });

  if (!ARCHIVE_ROLES.has(currentUser?.role ?? '')) return null;

  return (
    <>
      <Button variant="secondary" size={size} onClick={() => setConfirmOpen(true)}>
        {archived ? 'Unarchive' : 'Archive'}
      </Button>
      <Modal
        open={confirmOpen}
        onClose={() => { if (!mutation.isPending) setConfirmOpen(false); }}
        title={archived ? `Restore this ${noun}?` : `Archive this ${noun}?`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={mutation.isPending}>Cancel</Button>
            <Button variant={archived ? 'primary' : 'danger'} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {archived ? 'Restore' : 'Archive'}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
          {archived
            ? `This ${noun} will move back onto the active list.`
            : entityType === 'properties'
              ? 'The property and its parcels move off the active lists into the Archive section. They stay searchable and flagged as archived. Appeals must be complete first.'
              : 'The parcel moves off the active list into the Archive section. It stays searchable and flagged as archived. Its appeal must be complete first.'}
        </p>
      </Modal>
    </>
  );
}
